import { graphql } from "gatsby";
import React from "react";
import { SwiperSlide } from "swiper/react";

import Image from "../image";
import RichText from "../richText";
import OverviewSlider from "../slider";

type Props = {
  slice:
    | Queries.MarketingTreatmentUtilizationSliceFragment
    | Queries.TreatmentUtilizationSliceFragment
    | Queries.TreatmentAddonUtilizationSliceFragment
    | Queries.ProductUtilizationSliceFragment
    | Queries.ContentUtilizationSliceFragment
    | Queries.LocationUtilizationSliceFragment
    | Queries.DentistUtilizationSliceFragment;
};

const Utilization: React.FC<Props> = ({ slice }) => {
  const { primary, items } = slice;
  const { showLabel, title, subTitle } = primary;

  const listOfItems = items.map((item, index) => {
    const { itemTitle, itemText, itemImage } = item;

    return (
      <SwiperSlide
        key={index}
        virtualIndex={index}
        className="z-10 !h-auto min-w-[315px] max-w-[418px] overflow-hidden rounded-md border-b-0 bg-white"
      >
        <div className="overflow-hidden relative rounded-md">
          <Image image={itemImage} lazy={false} className="w-full aspect-[3/2]" />
          {showLabel && (
            <div className="flex absolute top-2 left-2 z-30 justify-center items-center w-10 h-10 bg-white rounded-full typo-h3 swiper-slide_absolute">
              {index + 1}
            </div>
          )}
        </div>
        <span className="block typo-h5 m-t-xs-fluid m-r-sm-fluid">{itemTitle}</span>
        <RichText data={itemText?.richText} className="block mt-2 typo-base m-r-sm-fluid text-black/70" />
      </SwiperSlide>
    );
  });

  return (
    <section className="px-5 p-t-xxxl-fluid p-b-xxxl-fluid">
      <div className="container px-0 max-w-screen-xl">
        <RichText data={title?.richText} className="block mb-0 font-medium m-b-xs-fluid" />
        <RichText data={subTitle?.richText} className="block mb-0 typo-base text-black/70" />
        <div className="baseGrid m-t-md-fluid lg:gap-x-lg">
          {items.length > 0 && <OverviewSlider slides={listOfItems} type="utilization" initialSlides={3} />}
        </div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment LocationUtilizationSlice on PrismicLocationDataBodyUtilization {
    ...Slice

    primary {
      showLabel: utilization_image_label
      title: utilization_title {
        ...RichText
      }
      subTitle: utilization_subtitle {
        ...RichText
      }
    }
    items {
      itemTitle: utilization_item_title
      itemText: utilization_item_text {
        ...RichText
      }
      itemImage: utilization_item_image {
        alt
        gatsbyImageData(height: 276, placeholder: BLURRED)
      }
    }
  }

  fragment ContentUtilizationSlice on PrismicPageContentDataBodyUtilization {
    ...Slice

    primary {
      showLabel: utilization_image_label
      title: utilization_title {
        ...RichText
      }
      subTitle: utilization_subtitle {
        ...RichText
      }
    }
    items {
      itemTitle: utilization_item_title
      itemText: utilization_item_text {
        ...RichText
      }
      itemImage: utilization_item_image {
        alt
        gatsbyImageData(height: 276, placeholder: BLURRED)
      }
    }
  }

  fragment DentistUtilizationSlice on PrismicPageDentistDataBodyUtilization {
    ...Slice

    primary {
      showLabel: utilization_image_label
      title: utilization_title {
        ...RichText
      }
      subTitle: utilization_subtitle {
        ...RichText
      }
    }
    items {
      itemTitle: utilization_item_title
      itemText: utilization_item_text {
        ...RichText
      }
      itemImage: utilization_item_image {
        alt
        gatsbyImageData(height: 276, placeholder: BLURRED)
      }
    }
  }

  fragment MarketingTreatmentUtilizationSlice on PrismicMarketingTreatmentDataBodyUtilization {
    ...Slice

    primary {
      showLabel: utilization_image_label
      title: utilization_title {
        ...RichText
      }
      subTitle: utilization_subtitle {
        ...RichText
      }
    }
    items {
      itemTitle: utilization_item_title
      itemText: utilization_item_text {
        ...RichText
      }
      itemImage: utilization_item_image {
        alt
        gatsbyImageData(height: 276, placeholder: BLURRED)
      }
    }
  }

  fragment TreatmentUtilizationSlice on PrismicTreatmentDataBodyUtilization {
    ...Slice

    primary {
      showLabel: utilization_image_label
      title: utilization_title {
        ...RichText
      }
      subTitle: utilization_subtitle {
        ...RichText
      }
    }
    items {
      itemTitle: utilization_item_title
      itemText: utilization_item_text {
        ...RichText
      }
      itemImage: utilization_item_image {
        alt
        gatsbyImageData(height: 276, placeholder: BLURRED)
      }
    }
  }

  fragment TreatmentAddonUtilizationSlice on PrismicTreatmentAddonDataBodyUtilization {
    ...Slice

    primary {
      showLabel: utilization_image_label
      title: utilization_title {
        ...RichText
      }
      subTitle: utilization_subtitle {
        ...RichText
      }
    }
    items {
      itemTitle: utilization_item_title
      itemText: utilization_item_text {
        ...RichText
      }
      itemImage: utilization_item_image {
        alt
        gatsbyImageData(height: 276, placeholder: BLURRED)
      }
    }
  }

  fragment ProductUtilizationSlice on PrismicProductDataBodyUtilization {
    ...Slice

    primary {
      showLabel: utilization_image_label
      title: utilization_title {
        ...RichText
      }
      subTitle: utilization_subtitle {
        ...RichText
      }
    }
    items {
      itemTitle: utilization_item_title
      itemText: utilization_item_text {
        ...RichText
      }
      itemImage: utilization_item_image {
        alt
        gatsbyImageData(height: 276, placeholder: BLURRED)
      }
    }
  }
`;
export default Utilization;
